// src/components/tools/school-strategic/SchoolStrategicTool.jsx
// Strategic Management Tool for South African Schools - SSE & SIP
import React, { useState, useEffect, useMemo } from 'react';
import {
  ArrowLeft, FileText, Target, CheckCircle, AlertCircle, Plus, X, Save,
  Users, BarChart3, Calendar, Clock, Edit2, Trash2, Eye, Download,
  ChevronRight, ChevronDown, ArrowRight, TrendingUp, Activity, Search,
  Filter, MoreVertical, Star, AlertTriangle, ClipboardList, Building
} from 'lucide-react';
import { toolsService } from '../../../services/toolsService';

// ============================================================
// CONSTANTS & CONFIGURATIONS
// ============================================================

const ViewState = {
  DASHBOARD: 'DASHBOARD',
  SSE_LIST: 'SSE_LIST',
  SSE_FORM: 'SSE_FORM',
  SSE_DETAIL: 'SSE_DETAIL',
  SIP_LIST: 'SIP_LIST',
  SIP_FORM: 'SIP_FORM',
  SIP_DETAIL: 'SIP_DETAIL',
  SIP_PROGRESS: 'SIP_PROGRESS',
  SIP_FROM_SSE: 'SIP_FROM_SSE'
};

// WSE Areas based on South African DBE Policy
const WSE_AREAS = {
  basicFunctionality: { 
    name: 'Basic Functionality', 
    code: 'BF',
    description: 'School operates effectively with basic systems in place'
  },
  qualityTeachingLearning: { 
    name: 'Quality of Teaching & Learning', 
    code: 'QTLED',
    description: 'Teaching practices and learning outcomes'
  },
  learnerAchievement: { 
    name: 'Learner Achievement', 
    code: 'LA',
    description: 'Academic performance and learner outcomes'
  },
  leadershipManagement: { 
    name: 'Leadership, Management & Communication', 
    code: 'LMC',
    description: 'School leadership and management effectiveness'
  },
  curriculumProvision: { 
    name: 'Curriculum Provision & Resources', 
    code: 'CPR',
    description: 'Curriculum delivery and resource availability'
  },
  governance: { 
    name: 'Governance & Relationships', 
    code: 'GR',
    description: 'School governance and stakeholder relationships'
  },
  schoolSafety: { 
    name: 'School Safety, Security & Discipline', 
    code: 'SSSD',
    description: 'Safety measures and discipline systems'
  },
  schoolInfrastructure: { 
    name: 'School Infrastructure', 
    code: 'SI',
    description: 'Physical infrastructure and facilities'
  },
  parentsCommunity: { 
    name: 'Parents & Community', 
    code: 'PC',
    description: 'Parent involvement and community engagement'
  }
};

const SCORE_LABELS = {
  1: { label: 'Needs Urgent Support', color: 'red' },
  2: { label: 'Needs Improvement', color: 'orange' },
  3: { label: 'Acceptable', color: 'amber' },
  4: { label: 'Good', color: 'lime' },
  5: { label: 'Outstanding', color: 'green' }
};

const TERMS = ['Term 1', 'Term 2', 'Term 3', 'Term 4'];
const QUARTERS = ['T1', 'T2', 'T3', 'T4'];

// ============================================================
// HELPER FUNCTIONS
// ============================================================

const getScoreColor = (score) => {
  if (score >= 4) return 'green';
  if (score >= 3) return 'amber';
  if (score >= 2) return 'orange';
  return 'red';
};

const getStatusBadge = (status) => {
  const configs = {
    draft: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Draft' },
    completed: { bg: 'bg-green-100', text: 'text-green-700', label: 'Completed' },
    'in-progress': { bg: 'bg-blue-100', text: 'text-blue-700', label: 'In Progress' }
  };
  return configs[status] || configs.draft;
};

const getCurrentTerm = () => {
  const month = new Date().getMonth() + 1;
  if (month >= 1 && month <= 3) return 'T1';
  if (month >= 4 && month <= 6) return 'T2';
  if (month >= 7 && month <= 9) return 'T3';
  return 'T4';
};

const formatDate = (date) => {
  if (!date) return '-';
  const d = date.toDate ? date.toDate() : new Date(date);
  return d.toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' });
};

const getCurrentAcademicYear = () => {
  const now = new Date();
  return now.getMonth() >= 0 ? now.getFullYear().toString() : (now.getFullYear() - 1).toString();
};

// ============================================================
// SHARED UI COMPONENTS
// ============================================================

const StatCard = ({ icon: Icon, label, value, subtitle, color = 'indigo', onClick }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-100 hover:border-gray-200 hover:shadow-sm transition-all text-left w-full ${onClick ? 'cursor-pointer' : ''}`}
  >
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-${color}-50`}>
      <Icon className={`w-6 h-6 text-${color}-600`} />
    </div>
    <div className="flex-1">
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-sm text-gray-500">{label}</p>
      {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
    </div>
    {onClick && <ChevronRight className="w-5 h-5 text-gray-300" />}
  </button>
);

const EmptyState = ({ icon: Icon, title, description, action }) => (
  <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
      <Icon className="w-8 h-8 text-gray-400" />
    </div>
    <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
    <p className="text-gray-500 mb-6 max-w-sm">{description}</p>
    {action}
  </div>
);

const ScoreIndicator = ({ score, size = 'md' }) => {
  const color = getScoreColor(score);
  const sizeClasses = {
    sm: 'w-8 h-8 text-sm',
    md: 'w-10 h-10 text-base',
    lg: 'w-12 h-12 text-lg'
  };
  
  return (
    <div className={`${sizeClasses[size]} rounded-full flex items-center justify-center font-bold bg-${color}-100 text-${color}-700`}>
      {score}
    </div>
  );
};

const ProgressBar = ({ value, color = 'indigo', showLabel = true }) => (
  <div className="w-full">
    <div className="flex justify-between items-center mb-1">
      {showLabel && <span className="text-sm text-gray-600">{value}% Complete</span>}
    </div>
    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
      <div 
        className={`h-full bg-${color}-500 rounded-full transition-all duration-500`}
        style={{ width: `${value}%` }}
      />
    </div>
  </div>
);

// ============================================================
// SSE ASSESSMENT FORM
// ============================================================

const SSEAssessmentForm = ({ bookId, assessment, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    academicYear: getCurrentAcademicYear(),
    term: 'Term 1',
    teamMembers: [],
    areas: {},
    recommendations: '',
    priorityAreas: [],
    overallScore: 0,
    status: 'draft',
    ...assessment
  });
  
  const [currentArea, setCurrentArea] = useState('basicFunctionality');
  const [newMember, setNewMember] = useState('');
  const [saving, setSaving] = useState(false);

  // Initialize areas if empty
  useEffect(() => {
    if (Object.keys(formData.areas).length === 0) {
      const initialAreas = {};
      Object.keys(WSE_AREAS).forEach(key => {
        initialAreas[key] = { score: 0, status: 'red', comments: '' };
      });
      setFormData(prev => ({ ...prev, areas: initialAreas }));
    }
  }, []);

  const addTeamMember = () => {
    if (newMember.trim() && !formData.teamMembers.includes(newMember.trim())) {
      setFormData(prev => ({
        ...prev,
        teamMembers: [...prev.teamMembers, newMember.trim()]
      }));
      setNewMember('');
    }
  };

  const removeTeamMember = (index) => {
    setFormData(prev => ({
      ...prev,
      teamMembers: prev.teamMembers.filter((_, i) => i !== index)
    }));
  };

  const updateAreaScore = (areaKey, score) => {
    const numScore = parseInt(score);
    setFormData(prev => {
      const newAreas = {
        ...prev.areas,
        [areaKey]: {
          ...prev.areas[areaKey],
          score: numScore,
          status: numScore >= 4 ? 'green' : numScore >= 3 ? 'amber' : 'red'
        }
      };

      // Calculate overall score
      const scores = Object.values(newAreas).map(a => a.score).filter(s => s > 0);
      const overallScore = scores.length > 0 
        ? Math.round((scores.reduce((a, b) => a + b, 0) / scores.length) * 10) / 10
        : 0;

      // Identify priority areas (score < 3)
      const priorityAreas = Object.entries(newAreas)
        .filter(([_, area]) => area.score > 0 && area.score < 3)
        .map(([key]) => WSE_AREAS[key].name);

      return { ...prev, areas: newAreas, overallScore, priorityAreas };
    });
  };

  const handleSave = async (status = 'draft') => {
    setSaving(true);
    try {
      await onSave({ ...formData, status });
    } finally {
      setSaving(false);
    }
  };

  const areaKeys = Object.keys(WSE_AREAS);
  const currentIndex = areaKeys.indexOf(currentArea);
  const progress = Math.round(
    (Object.values(formData.areas).filter(a => a.score > 0).length / areaKeys.length) * 100
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={onCancel} className="p-2 hover:bg-gray-100 rounded-lg">
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              {assessment?.id ? 'Edit' : 'New'} SSE Assessment
            </h2>
            <p className="text-sm text-gray-500">School Self-Evaluation based on WSE Policy</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleSave('draft')}
            disabled={saving}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg flex items-center gap-2"
          >
            <Save className="w-4 h-4" /> Save Draft
          </button>
          <button
            onClick={() => handleSave('completed')}
            disabled={saving || progress < 100}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2 disabled:opacity-50"
          >
            <CheckCircle className="w-4 h-4" /> Complete
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar */}
        <div className="lg:col-span-1 space-y-4">
          {/* Basic Info */}
          <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-4">
            <h3 className="font-medium text-gray-900">Assessment Details</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Academic Year *</label>
              <input
                type="text"
                value={formData.academicYear}
                onChange={(e) => setFormData(prev => ({ ...prev, academicYear: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                placeholder="2024"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Term</label>
              <select
                value={formData.term}
                onChange={(e) => setFormData(prev => ({ ...prev, term: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
              >
                {TERMS.map(term => <option key={term} value={term}>{term}</option>)}
              </select>
            </div>

            {/* Team Members */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">SSE Team</label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={newMember}
                  onChange={(e) => setNewMember(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTeamMember())}
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm"
                  placeholder="Add member..."
                />
                <button onClick={addTeamMember} className="p-2 bg-indigo-600 text-white rounded-lg">
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.teamMembers.map((member, i) => (
                  <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-50 text-indigo-700 rounded-full text-xs">
                    {member}
                    <button onClick={() => removeTeamMember(i)} className="hover:text-indigo-900">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>

            {/* Progress */}
            <div className="pt-4 border-t border-gray-100">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-600">Progress</span>
                <span className="font-medium">{progress}%</span>
              </div>
              <ProgressBar value={progress} showLabel={false} />
            </div>

            {/* Overall Score */}
            {formData.overallScore > 0 && (
              <div className="pt-4 border-t border-gray-100 text-center">
                <p className="text-sm text-gray-600 mb-2">Overall Score</p>
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full text-2xl font-bold bg-${getScoreColor(formData.overallScore)}-100 text-${getScoreColor(formData.overallScore)}-700`}>
                  {formData.overallScore}
                </div>
              </div>
            )}
          </div>

          {/* Area Navigation */}
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <h3 className="font-medium text-gray-900 mb-3">WSE Areas</h3>
            <div className="space-y-1">
              {areaKeys.map((key) => {
                const area = WSE_AREAS[key];
                const score = formData.areas[key]?.score || 0;
                return (
                  <button
                    key={key}
                    onClick={() => setCurrentArea(key)}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-left text-sm transition-all ${
                      currentArea === key ? 'bg-indigo-50 text-indigo-700' : 'hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    <span className="truncate">{area.code}</span>
                    {score > 0 && <ScoreIndicator score={score} size="sm" />}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Main Form */}
        <div className="lg:col-span-3">
          <div className="bg-white rounded-xl border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {WSE_AREAS[currentArea].name}
                </h3>
                <p className="text-sm text-gray-500">{WSE_AREAS[currentArea].description}</p>
              </div>
              <span className="text-sm text-gray-400">
                Area {currentIndex + 1} of {areaKeys.length}
              </span>
            </div>

            {/* Score Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Assessment Score (1-5)
              </label>
              <div className="grid grid-cols-5 gap-3">
                {[1, 2, 3, 4, 5].map(score => (
                  <button
                    key={score}
                    onClick={() => updateAreaScore(currentArea, score)}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      formData.areas[currentArea]?.score === score
                        ? `border-${SCORE_LABELS[score].color}-500 bg-${SCORE_LABELS[score].color}-50`
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-2xl font-bold mb-1">{score}</div>
                    <div className="text-xs text-gray-600">{SCORE_LABELS[score].label}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Comments */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Comments & Evidence
              </label>
              <textarea
                value={formData.areas[currentArea]?.comments || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  areas: {
                    ...prev.areas,
                    [currentArea]: { ...prev.areas[currentArea], comments: e.target.value }
                  }
                }))}
                rows={4}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500"
                placeholder="Provide evidence and comments to support this score..."
              />
            </div>

            {/* Navigation */}
            <div className="flex justify-between pt-4 border-t border-gray-100">
              <button
                onClick={() => setCurrentArea(areaKeys[Math.max(0, currentIndex - 1)])}
                disabled={currentIndex === 0}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50 flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" /> Previous
              </button>
              <button
                onClick={() => setCurrentArea(areaKeys[Math.min(areaKeys.length - 1, currentIndex + 1)])}
                disabled={currentIndex === areaKeys.length - 1}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
              >
                Next <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Recommendations (show when all areas scored) */}
          {progress === 100 && (
            <div className="bg-white rounded-xl border border-gray-100 p-6 mt-6">
              <h3 className="font-semibold text-gray-900 mb-4">Overall Recommendations</h3>
              <textarea
                value={formData.recommendations}
                onChange={(e) => setFormData(prev => ({ ...prev, recommendations: e.target.value }))}
                rows={4}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl"
                placeholder="Provide overall recommendations based on the assessment..."
              />

              {formData.priorityAreas.length > 0 && (
                <div className="mt-4 p-4 bg-amber-50 rounded-lg">
                  <h4 className="font-medium text-amber-800 mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" /> Priority Areas Identified
                  </h4>
                  <ul className="list-disc list-inside text-sm text-amber-700 space-y-1">
                    {formData.priorityAreas.map((area, i) => (
                      <li key={i}>{area}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================
// SSE DETAIL VIEW
// ============================================================

const SSEDetailView = ({ assessment, onEdit, onCreateSIP, onBack }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const statusBadge = getStatusBadge(assessment.status);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-lg">
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              SSE Assessment - {assessment.academicYear} {assessment.term}
            </h2>
            <div className="flex items-center gap-2 mt-1">
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge.bg} ${statusBadge.text}`}>
                {statusBadge.label}
              </span>
              <span className="text-sm text-gray-500">
                Created {formatDate(assessment.createdAt)}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onEdit(assessment)}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg flex items-center gap-2"
          >
            <Edit2 className="w-4 h-4" /> Edit
          </button>
          {assessment.status === 'completed' && (
            <button
              onClick={() => onCreateSIP(assessment)}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2"
            >
              <Target className="w-4 h-4" /> Create SIP
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-6">
          {['overview', 'areas', 'recommendations'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-3 text-sm font-medium border-b-2 transition-all ${
                activeTab === tab
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      <div className="bg-white rounded-xl border border-gray-100 p-6">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Score Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center p-6 bg-gray-50 rounded-xl">
                <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full text-3xl font-bold mb-3 bg-${getScoreColor(assessment.overallScore)}-100 text-${getScoreColor(assessment.overallScore)}-700`}>
                  {assessment.overallScore}
                </div>
                <p className="text-gray-600">Overall Score</p>
              </div>
              <div className="p-6 bg-gray-50 rounded-xl">
                <h4 className="font-medium text-gray-900 mb-3">Team Members</h4>
                <div className="flex flex-wrap gap-2">
                  {assessment.teamMembers?.map((member, i) => (
                    <span key={i} className="px-2 py-1 bg-white rounded text-sm text-gray-700">
                      {member}
                    </span>
                  ))}
                </div>
              </div>
              <div className="p-6 bg-gray-50 rounded-xl">
                <h4 className="font-medium text-gray-900 mb-3">Priority Areas</h4>
                {assessment.priorityAreas?.length > 0 ? (
                  <ul className="text-sm text-gray-600 space-y-1">
                    {assessment.priorityAreas.map((area, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <AlertTriangle className="w-3 h-3 text-amber-500" /> {area}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-500">No priority areas identified</p>
                )}
              </div>
            </div>

            {/* Quick Scores */}
            <div>
              <h4 className="font-medium text-gray-900 mb-3">WSE Area Scores</h4>
              <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
                {Object.entries(WSE_AREAS).map(([key, area]) => (
                  <div key={key} className="text-center p-3 bg-gray-50 rounded-lg">
                    <ScoreIndicator score={assessment.areas?.[key]?.score || 0} size="md" />
                    <p className="text-xs text-gray-600 mt-2">{area.code}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'areas' && (
          <div className="space-y-4">
            {Object.entries(WSE_AREAS).map(([key, area]) => {
              const areaData = assessment.areas?.[key] || {};
              return (
                <div key={key} className="border border-gray-100 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h4 className="font-medium text-gray-900">{area.name}</h4>
                      <p className="text-sm text-gray-500">{area.code}</p>
                    </div>
                    <ScoreIndicator score={areaData.score || 0} />
                  </div>
                  {areaData.comments && (
                    <p className="text-sm text-gray-600 mt-3 p-3 bg-gray-50 rounded-lg">
                      {areaData.comments}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {activeTab === 'recommendations' && (
          <div>
            <h4 className="font-medium text-gray-900 mb-3">Overall Recommendations</h4>
            {assessment.recommendations ? (
              <div className="p-4 bg-gray-50 rounded-xl text-gray-700 whitespace-pre-wrap">
                {assessment.recommendations}
              </div>
            ) : (
              <p className="text-gray-500">No recommendations provided</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================
// SIP FORM
// ============================================================

const SIPForm = ({ bookId, plan, sseAssessment, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    academicYear: getCurrentAcademicYear(),
    title: '',
    basedOnSSE: sseAssessment?.id || '',
    schoolContext: '',
    visionMission: '',
    sectionA: [], // Core Functions (max 8)
    sectionB: [], // Enhancement
    status: 'draft',
    ...plan
  });

  const [activeSection, setActiveSection] = useState('context');
  const [saving, setSaving] = useState(false);

  // Initialize from SSE if provided
  useEffect(() => {
    if (sseAssessment && !plan?.id) {
      const objectives = generateObjectivesFromSSE(sseAssessment);
      setFormData(prev => ({
        ...prev,
        academicYear: sseAssessment.academicYear,
        title: `School Improvement Plan ${sseAssessment.academicYear}`,
        basedOnSSE: sseAssessment.id,
        schoolContext: `This SIP is based on the SSE assessment conducted for ${sseAssessment.academicYear} ${sseAssessment.term}, which identified priority areas for improvement.`,
        sectionA: objectives
      }));
    }
  }, [sseAssessment]);

  const generateObjectivesFromSSE = (assessment) => {
    if (!assessment?.priorityAreas?.length) return [];

    return assessment.priorityAreas.slice(0, 8).map((area, index) => ({
      id: `obj_${Date.now()}_${index}`,
      title: `Improve ${area}`,
      description: `Address deficiencies in ${area} as identified in the SSE assessment.`,
      successCriteria: `Achieve a score of 4 or higher in ${area} in the next SSE.`,
      responsiblePerson: '',
      timeline: 'Annual',
      resources: '',
      activities: [
        {
          id: `act_${Date.now()}_${index}_1`,
          description: `Conduct detailed analysis of ${area} challenges`,
          timeline: 'Term 1',
          responsiblePerson: '',
          resources: 'Staff time, assessment tools',
          quarterlyStatus: { T1: 'red', T2: 'red', T3: 'red', T4: 'red' }
        },
        {
          id: `act_${Date.now()}_${index}_2`,
          description: `Develop and implement improvement strategies for ${area}`,
          timeline: 'Term 2-4',
          responsiblePerson: '',
          resources: 'Implementation budget',
          quarterlyStatus: { T1: 'red', T2: 'red', T3: 'red', T4: 'red' }
        }
      ]
    }));
  };

  const addObjective = (section) => {
    const newObj = {
      id: `obj_${Date.now()}`,
      title: '',
      description: '',
      successCriteria: '',
      responsiblePerson: '',
      timeline: 'Annual',
      resources: '',
      activities: []
    };
    setFormData(prev => ({
      ...prev,
      [section]: [...prev[section], newObj]
    }));
  };

  const updateObjective = (section, objId, updates) => {
    setFormData(prev => ({
      ...prev,
      [section]: prev[section].map(obj => obj.id === objId ? { ...obj, ...updates } : obj)
    }));
  };

  const removeObjective = (section, objId) => {
    setFormData(prev => ({
      ...prev,
      [section]: prev[section].filter(obj => obj.id !== objId)
    }));
  };

  const addActivity = (section, objId) => {
    const obj = formData[section].find(o => o.id === objId);
    const newActivity = {
      id: `act_${Date.now()}`,
      description: '',
      timeline: '',
      responsiblePerson: '',
      resources: '',
      quarterlyStatus: { T1: 'red', T2: 'red', T3: 'red', T4: 'red' }
    };
    updateObjective(section, objId, { activities: [...(obj.activities || []), newActivity] });
  };

  const handleSave = async (status = 'draft') => {
    setSaving(true);
    try {
      await onSave({ ...formData, status });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={onCancel} className="p-2 hover:bg-gray-100 rounded-lg">
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              {plan?.id ? 'Edit' : 'Create'} SIP Plan
            </h2>
            <p className="text-sm text-gray-500">School Improvement Plan</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleSave('draft')}
            disabled={saving}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg flex items-center gap-2"
          >
            <Save className="w-4 h-4" /> Save Draft
          </button>
          <button
            onClick={() => handleSave('in-progress')}
            disabled={saving}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2"
          >
            <CheckCircle className="w-4 h-4" /> Finalize
          </button>
        </div>
      </div>

      {/* Section Navigation */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {[
          { key: 'context', label: 'School Context', icon: FileText },
          { key: 'sectionA', label: 'Core Functions', icon: Target },
          { key: 'sectionB', label: 'Enhancement', icon: Plus }
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setActiveSection(key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-all ${
              activeSection === key
                ? 'bg-indigo-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
            {key !== 'context' && (
              <span className={`px-1.5 py-0.5 rounded text-xs ${
                activeSection === key ? 'bg-indigo-500' : 'bg-gray-200'
              }`}>
                {formData[key]?.length || 0}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="bg-white rounded-xl border border-gray-100 p-6">
        {activeSection === 'context' && (
          <div className="space-y-4 max-w-2xl">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Academic Year *</label>
              <input
                type="text"
                value={formData.academicYear}
                onChange={(e) => setFormData(prev => ({ ...prev, academicYear: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-200 rounded-xl"
                placeholder="2024"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">SIP Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-200 rounded-xl"
                placeholder="School Improvement Plan 2024"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">School Context</label>
              <textarea
                value={formData.schoolContext}
                onChange={(e) => setFormData(prev => ({ ...prev, schoolContext: e.target.value }))}
                rows={4}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl"
                placeholder="Describe the school's context, challenges, and opportunities..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Vision & Mission Alignment</label>
              <textarea
                value={formData.visionMission}
                onChange={(e) => setFormData(prev => ({ ...prev, visionMission: e.target.value }))}
                rows={3}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl"
                placeholder="How does this SIP align with the school's vision?"
              />
            </div>
          </div>
        )}

        {(activeSection === 'sectionA' || activeSection === 'sectionB') && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900">
                  {activeSection === 'sectionA' ? 'Section A: Core Functions' : 'Section B: Enhancement'}
                </h3>
                <p className="text-sm text-gray-500">
                  {activeSection === 'sectionA' ? 'Maximum 8 objectives for core functions' : 'Additional improvement objectives'}
                </p>
              </div>
              <button
                onClick={() => addObjective(activeSection)}
                disabled={activeSection === 'sectionA' && formData.sectionA.length >= 8}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2 disabled:opacity-50"
              >
                <Plus className="w-4 h-4" /> Add Objective
              </button>
            </div>

            {formData[activeSection].length === 0 ? (
              <EmptyState
                icon={Target}
                title="No objectives yet"
                description="Add objectives to define your improvement goals."
                action={
                  <button
                    onClick={() => addObjective(activeSection)}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg"
                  >
                    <Plus className="w-4 h-4 inline mr-2" /> Add First Objective
                  </button>
                }
              />
            ) : (
              <div className="space-y-4">
                {formData[activeSection].map((obj, index) => (
                  <ObjectiveEditor
                    key={obj.id}
                    objective={obj}
                    index={index}
                    onUpdate={(updates) => updateObjective(activeSection, obj.id, updates)}
                    onRemove={() => removeObjective(activeSection, obj.id)}
                    onAddActivity={() => addActivity(activeSection, obj.id)}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Objective Editor Component
const ObjectiveEditor = ({ objective, index, onUpdate, onRemove, onAddActivity }) => {
  const [expanded, setExpanded] = useState(true);

  const updateActivity = (actId, updates) => {
    onUpdate({
      activities: objective.activities.map(act => 
        act.id === actId ? { ...act, ...updates } : act
      )
    });
  };

  const removeActivity = (actId) => {
    onUpdate({
      activities: objective.activities.filter(act => act.id !== actId)
    });
  };

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <div 
        className="flex items-center gap-3 p-4 bg-gray-50 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <button className="text-gray-400">
          {expanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
        </button>
        <span className="w-8 h-8 bg-indigo-100 text-indigo-700 rounded-lg flex items-center justify-center font-medium text-sm">
          {index + 1}
        </span>
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-gray-900 truncate">
            {objective.title || 'Untitled Objective'}
          </h4>
          <p className="text-sm text-gray-500">{objective.activities?.length || 0} activities</p>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {expanded && (
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Objective Title *</label>
              <input
                type="text"
                value={objective.title}
                onChange={(e) => onUpdate({ title: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                placeholder="e.g., Improve learner achievement"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Responsible Person</label>
              <input
                type="text"
                value={objective.responsiblePerson || ''}
                onChange={(e) => onUpdate({ responsiblePerson: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                placeholder="e.g., HOD Mathematics"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
            <textarea
              value={objective.description}
              onChange={(e) => onUpdate({ description: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg"
              placeholder="Describe what this objective aims to achieve..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Success Criteria</label>
            <textarea
              value={objective.successCriteria || ''}
              onChange={(e) => onUpdate({ successCriteria: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg"
              placeholder="How will success be measured?"
            />
          </div>

          {/* Activities */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-gray-700">Activities</label>
              <button
                onClick={onAddActivity}
                className="text-sm text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
              >
                <Plus className="w-4 h-4" /> Add Activity
              </button>
            </div>

            <div className="space-y-3">
              {objective.activities?.map((activity, actIndex) => (
                <div key={activity.id} className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-start gap-3">
                    <span className="text-sm text-gray-400 mt-2">{actIndex + 1}.</span>
                    <div className="flex-1 space-y-2">
                      <input
                        type="text"
                        value={activity.description}
                        onChange={(e) => updateActivity(activity.id, { description: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                        placeholder="Activity description..."
                      />
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        <input
                          type="text"
                          value={activity.timeline || ''}
                          onChange={(e) => updateActivity(activity.id, { timeline: e.target.value })}
                          className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
                          placeholder="Timeline"
                        />
                        <input
                          type="text"
                          value={activity.responsiblePerson || ''}
                          onChange={(e) => updateActivity(activity.id, { responsiblePerson: e.target.value })}
                          className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
                          placeholder="Responsible"
                        />
                        <input
                          type="text"
                          value={activity.resources || ''}
                          onChange={(e) => updateActivity(activity.id, { resources: e.target.value })}
                          className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm col-span-2"
                          placeholder="Resources needed"
                        />
                      </div>
                    </div>
                    <button
                      onClick={() => removeActivity(activity.id)}
                      className="p-1.5 text-gray-400 hover:text-red-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================
// SIP PROGRESS TRACKING
// ============================================================

const SIPProgressView = ({ plan, onUpdate, onBack }) => {
  const [selectedTerm, setSelectedTerm] = useState(getCurrentTerm());

  const calculateProgress = () => {
    let completed = 0;
    let total = 0;

    [...(plan.sectionA || []), ...(plan.sectionB || [])].forEach(obj => {
      obj.activities?.forEach(act => {
        total++;
        if (act.quarterlyStatus?.[selectedTerm] === 'green') completed++;
      });
    });

    return { completed, total, percentage: total > 0 ? Math.round((completed / total) * 100) : 0 };
  };

  const updateActivityStatus = async (objId, actId, status) => {
    const updatedPlan = { ...plan };
    
    ['sectionA', 'sectionB'].forEach(section => {
      updatedPlan[section] = updatedPlan[section]?.map(obj => {
        if (obj.id === objId) {
          return {
            ...obj,
            activities: obj.activities?.map(act => {
              if (act.id === actId) {
                return {
                  ...act,
                  quarterlyStatus: { ...act.quarterlyStatus, [selectedTerm]: status }
                };
              }
              return act;
            })
          };
        }
        return obj;
      });
    });

    await onUpdate(updatedPlan);
  };

  const progress = calculateProgress();
  const statusColors = { red: 'bg-red-500', amber: 'bg-amber-500', green: 'bg-green-500' };
  const statusLabels = { red: 'Not Started', amber: 'In Progress', green: 'Completed' };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-lg">
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h2 className="text-xl font-bold text-gray-900">SIP Progress Tracking</h2>
            <p className="text-sm text-gray-500">{plan.title} - {plan.academicYear}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {QUARTERS.map(q => (
            <button
              key={q}
              onClick={() => setSelectedTerm(q)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                selectedTerm === q
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {q}
            </button>
          ))}
        </div>
      </div>

      {/* Progress Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-100 p-6 text-center">
          <div className="text-4xl font-bold text-indigo-600 mb-2">{progress.percentage}%</div>
          <p className="text-gray-600">Overall Progress</p>
          <ProgressBar value={progress.percentage} showLabel={false} />
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-6 text-center">
          <div className="text-4xl font-bold text-green-600 mb-2">{progress.completed}</div>
          <p className="text-gray-600">Activities Completed</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-6 text-center">
          <div className="text-4xl font-bold text-gray-600 mb-2">{progress.total - progress.completed}</div>
          <p className="text-gray-600">Activities Remaining</p>
        </div>
      </div>

      {/* Objectives Progress */}
      {['sectionA', 'sectionB'].map(section => {
        const objectives = plan[section] || [];
        if (objectives.length === 0) return null;

        return (
          <div key={section} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-100">
              <h3 className="font-semibold text-gray-900">
                {section === 'sectionA' ? 'Section A: Core Functions' : 'Section B: Enhancement'}
              </h3>
            </div>
            <div className="p-6 space-y-6">
              {objectives.map((obj, objIndex) => (
                <div key={obj.id} className="border border-gray-100 rounded-xl p-4">
                  <h4 className="font-medium text-gray-900 mb-1">
                    Objective {objIndex + 1}: {obj.title}
                  </h4>
                  <p className="text-sm text-gray-500 mb-4">{obj.description}</p>

                  <div className="space-y-3">
                    {obj.activities?.map((activity, actIndex) => (
                      <div key={activity.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">
                            {actIndex + 1}. {activity.description}
                          </p>
                          <p className="text-xs text-gray-500">
                            Timeline: {activity.timeline} | Responsible: {activity.responsiblePerson || 'TBD'}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          {['red', 'amber', 'green'].map(status => (
                            <button
                              key={status}
                              onClick={() => updateActivityStatus(obj.id, activity.id, status)}
                              title={statusLabels[status]}
                              className={`w-8 h-8 rounded-full border-2 transition-all ${
                                activity.quarterlyStatus?.[selectedTerm] === status
                                  ? `${statusColors[status]} border-gray-400`
                                  : 'bg-white border-gray-300 hover:border-gray-400'
                              }`}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ============================================================
// MAIN COMPONENT
// ============================================================

const SchoolStrategicTool = ({ bookId, customStyling = {} }) => {
  const [viewState, setViewState] = useState(ViewState.DASHBOARD);
  const [sseAssessments, setSSEAssessments] = useState([]);
  const [sipPlans, setSIPPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentItem, setCurrentItem] = useState(null);
  const [sseForSIP, setSSEForSIP] = useState(null);

  // Load data
  useEffect(() => {
    loadData();
  }, [bookId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [sse, sip] = await Promise.all([
        toolsService.getToolData(bookId, 'sse-assessments'),
        toolsService.getToolData(bookId, 'sip-plans')
      ]);
      setSSEAssessments(sse);
      setSIPPlans(sip);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // SSE Handlers
  const handleSaveSSE = async (data) => {
    try {
      await toolsService.saveToolData(bookId, 'sse-assessments', data);
      await loadData();
      setViewState(ViewState.SSE_LIST);
      setCurrentItem(null);
    } catch (error) {
      console.error('Error saving SSE:', error);
    }
  };

  const handleDeleteSSE = async (id) => {
    if (!window.confirm('Delete this SSE assessment?')) return;
    try {
      await toolsService.deleteToolData(bookId, 'sse-assessments', id);
      await loadData();
    } catch (error) {
      console.error('Error deleting SSE:', error);
    }
  };

  // SIP Handlers
  const handleSaveSIP = async (data) => {
    try {
      await toolsService.saveToolData(bookId, 'sip-plans', data);
      await loadData();
      setViewState(ViewState.SIP_LIST);
      setCurrentItem(null);
      setSSEForSIP(null);
    } catch (error) {
      console.error('Error saving SIP:', error);
    }
  };

  const handleDeleteSIP = async (id) => {
    if (!window.confirm('Delete this SIP plan?')) return;
    try {
      await toolsService.deleteToolData(bookId, 'sip-plans', id);
      await loadData();
    } catch (error) {
      console.error('Error deleting SIP:', error);
    }
  };

  const handleUpdateSIP = async (data) => {
    try {
      await toolsService.saveToolData(bookId, 'sip-plans', data);
      await loadData();
      // Update current item for progress view
      setCurrentItem(data);
    } catch (error) {
      console.error('Error updating SIP:', error);
    }
  };

  // Stats
  const stats = {
    sseTotal: sseAssessments.length,
    sseCompleted: sseAssessments.filter(s => s.status === 'completed').length,
    sipTotal: sipPlans.length,
    sipActive: sipPlans.filter(s => s.status === 'in-progress').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Render based on view state
  const renderContent = () => {
    switch (viewState) {
      case ViewState.SSE_FORM:
        return (
          <SSEAssessmentForm
            bookId={bookId}
            assessment={currentItem}
            onSave={handleSaveSSE}
            onCancel={() => { setViewState(ViewState.SSE_LIST); setCurrentItem(null); }}
          />
        );

      case ViewState.SSE_DETAIL:
        return (
          <SSEDetailView
            assessment={currentItem}
            onEdit={(sse) => { setCurrentItem(sse); setViewState(ViewState.SSE_FORM); }}
            onCreateSIP={(sse) => { setSSEForSIP(sse); setViewState(ViewState.SIP_FORM); }}
            onBack={() => { setViewState(ViewState.SSE_LIST); setCurrentItem(null); }}
          />
        );

      case ViewState.SSE_LIST:
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button onClick={() => setViewState(ViewState.DASHBOARD)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <ArrowLeft className="w-5 h-5 text-gray-600" />
                </button>
                <h2 className="text-xl font-bold text-gray-900">SSE Assessments</h2>
              </div>
              <button
                onClick={() => { setCurrentItem(null); setViewState(ViewState.SSE_FORM); }}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2"
              >
                <Plus className="w-4 h-4" /> New Assessment
              </button>
            </div>

            {sseAssessments.length === 0 ? (
              <EmptyState
                icon={ClipboardList}
                title="No SSE assessments yet"
                description="Create your first School Self-Evaluation assessment."
                action={
                  <button
                    onClick={() => { setCurrentItem(null); setViewState(ViewState.SSE_FORM); }}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg"
                  >
                    <Plus className="w-4 h-4 inline mr-2" /> Create Assessment
                  </button>
                }
              />
            ) : (
              <div className="grid gap-4">
                {sseAssessments.map(sse => {
                  const badge = getStatusBadge(sse.status);
                  return (
                    <div
                      key={sse.id}
                      className="bg-white rounded-xl border border-gray-100 p-4 hover:border-gray-200 hover:shadow-sm transition-all"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <ScoreIndicator score={sse.overallScore || 0} size="lg" />
                          <div>
                            <h3 className="font-semibold text-gray-900">
                              {sse.academicYear} {sse.term}
                            </h3>
                            <p className="text-sm text-gray-500">
                              {sse.priorityAreas?.length || 0} priority areas identified
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
                            {badge.label}
                          </span>
                          <button
                            onClick={() => { setCurrentItem(sse); setViewState(ViewState.SSE_DETAIL); }}
                            className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg"
                          >
                            <Eye className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => { setCurrentItem(sse); setViewState(ViewState.SSE_FORM); }}
                            className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg"
                          >
                            <Edit2 className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDeleteSSE(sse.id)}
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );

      case ViewState.SIP_FORM:
        return (
          <SIPForm
            bookId={bookId}
            plan={currentItem}
            sseAssessment={sseForSIP}
            onSave={handleSaveSIP}
            onCancel={() => { setViewState(ViewState.SIP_LIST); setCurrentItem(null); setSSEForSIP(null); }}
          />
        );

      case ViewState.SIP_PROGRESS:
        return (
          <SIPProgressView
            plan={currentItem}
            onUpdate={handleUpdateSIP}
            onBack={() => { setViewState(ViewState.SIP_LIST); setCurrentItem(null); }}
          />
        );

      case ViewState.SIP_LIST:
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button onClick={() => setViewState(ViewState.DASHBOARD)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <ArrowLeft className="w-5 h-5 text-gray-600" />
                </button>
                <h2 className="text-xl font-bold text-gray-900">SIP Plans</h2>
              </div>
              <button
                onClick={() => { setCurrentItem(null); setSSEForSIP(null); setViewState(ViewState.SIP_FORM); }}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2"
              >
                <Plus className="w-4 h-4" /> New SIP
              </button>
            </div>

            {sipPlans.length === 0 ? (
              <EmptyState
                icon={Target}
                title="No SIP plans yet"
                description="Create a School Improvement Plan to track your improvement objectives."
                action={
                  <button
                    onClick={() => { setCurrentItem(null); setViewState(ViewState.SIP_FORM); }}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg"
                  >
                    <Plus className="w-4 h-4 inline mr-2" /> Create SIP
                  </button>
                }
              />
            ) : (
              <div className="grid gap-4">
                {sipPlans.map(sip => {
                  const badge = getStatusBadge(sip.status);
                  const objectiveCount = (sip.sectionA?.length || 0) + (sip.sectionB?.length || 0);
                  return (
                    <div
                      key={sip.id}
                      className="bg-white rounded-xl border border-gray-100 p-4 hover:border-gray-200 hover:shadow-sm transition-all"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-semibold text-gray-900">{sip.title}</h3>
                          <p className="text-sm text-gray-500">
                            {sip.academicYear} • {objectiveCount} objectives
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
                            {badge.label}
                          </span>
                          <button
                            onClick={() => { setCurrentItem(sip); setViewState(ViewState.SIP_PROGRESS); }}
                            className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg"
                            title="Track Progress"
                          >
                            <Activity className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => { setCurrentItem(sip); setViewState(ViewState.SIP_FORM); }}
                            className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg"
                          >
                            <Edit2 className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDeleteSIP(sip.id)}
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );

      // Dashboard (default)
      default:
        return (
          <div className="space-y-6">
            {/* Header */}
            <div>
              <h1 className="text-2xl font-bold text-gray-900">School Strategic Management</h1>
              <p className="text-gray-500 mt-1">SSE Assessment & School Improvement Planning</p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard
                icon={ClipboardList}
                label="SSE Assessments"
                value={stats.sseTotal}
                subtitle={`${stats.sseCompleted} completed`}
                color="blue"
                onClick={() => setViewState(ViewState.SSE_LIST)}
              />
              <StatCard
                icon={Target}
                label="SIP Plans"
                value={stats.sipTotal}
                subtitle={`${stats.sipActive} active`}
                color="green"
                onClick={() => setViewState(ViewState.SIP_LIST)}
              />
              <StatCard
                icon={TrendingUp}
                label="Latest Score"
                value={sseAssessments[0]?.overallScore || '-'}
                subtitle={sseAssessments[0]?.academicYear || 'No assessments'}
                color="amber"
              />
              <StatCard
                icon={AlertTriangle}
                label="Priority Areas"
                value={sseAssessments[0]?.priorityAreas?.length || 0}
                subtitle="Needs attention"
                color="red"
              />
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                onClick={() => { setCurrentItem(null); setViewState(ViewState.SSE_FORM); }}
                className="flex items-center gap-4 p-6 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl border border-blue-200 hover:shadow-md transition-all text-left"
              >
                <div className="w-14 h-14 bg-blue-500 rounded-xl flex items-center justify-center">
                  <ClipboardList className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">New SSE Assessment</h3>
                  <p className="text-sm text-gray-600">Conduct a School Self-Evaluation</p>
                </div>
              </button>

              <button
                onClick={() => { setCurrentItem(null); setSSEForSIP(null); setViewState(ViewState.SIP_FORM); }}
                className="flex items-center gap-4 p-6 bg-gradient-to-br from-green-50 to-green-100 rounded-xl border border-green-200 hover:shadow-md transition-all text-left"
              >
                <div className="w-14 h-14 bg-green-500 rounded-xl flex items-center justify-center">
                  <Target className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">New SIP Plan</h3>
                  <p className="text-sm text-gray-600">Create a School Improvement Plan</p>
                </div>
              </button>
            </div>

            {/* Recent Activity */}
            {(sseAssessments.length > 0 || sipPlans.length > 0) && (
              <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                <div className="px-6 py-4 bg-gray-50 border-b border-gray-100">
                  <h3 className="font-semibold text-gray-900">Recent Activity</h3>
                </div>
                <div className="divide-y divide-gray-100">
                  {[...sseAssessments.slice(0, 2), ...sipPlans.slice(0, 2)]
                    .sort((a, b) => {
                      const dateA = a.updatedAt?.toDate?.() || new Date(a.updatedAt);
                      const dateB = b.updatedAt?.toDate?.() || new Date(b.updatedAt);
                      return dateB - dateA;
                    })
                    .slice(0, 4)
                    .map(item => (
                      <div key={item.id} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50">
                        <div className="flex items-center gap-3">
                          {item.overallScore !== undefined ? (
                            <ClipboardList className="w-5 h-5 text-blue-500" />
                          ) : (
                            <Target className="w-5 h-5 text-green-500" />
                          )}
                          <div>
                            <p className="font-medium text-gray-900">
                              {item.title || `SSE ${item.academicYear} ${item.term}`}
                            </p>
                            <p className="text-sm text-gray-500">
                              {formatDate(item.updatedAt || item.createdAt)}
                            </p>
                          </div>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(item.status).bg} ${getStatusBadge(item.status).text}`}>
                          {getStatusBadge(item.status).label}
                        </span>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        );
    }
  };

  return (
    <div className="h-full overflow-y-auto">
      <div className="p-6">
        {renderContent()}
      </div>
    </div>
  );
};

export default SchoolStrategicTool;